import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from "@angular/core/rxjs-interop";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { EmailTemplate } from "@models/email-templates/email-template";
import { EmailTemplateCodesToTypes, EmailTemplatesHelper } from "@models/email-templates/email-templates-helper";
import { TemplateSettingsBase } from "@models/email-templates/template-settings-base";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import { LoaderComponent } from "@shared/components/commons/loader/loader.component";
import { BaseListGridComponent } from "@shared/components/grid/components/base-list-grid/base-list-grid.component";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { SearchInputComponent } from "@shared/controls/search-input/search-input.component";
import { EmailsApiService } from "@shared/services/emails-api.service";
import { IPartialHeaderData } from "@shared/services/header.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { ColDef } from "ag-grid-community";
import { finalize, Observable, tap } from 'rxjs';
import {
  AgCoverLetterFileComponent
} from "../document-templates-page/components/ag-cover-letter-file/ag-cover-letter-file.component";
import {
  AgEmailTemplateActionsComponent
} from "./components/ag-email-template-actions/ag-email-template-actions.component";
import {
  EditEmailTemplateModalComponent
} from "./components/edit-email-template-modal/edit-email-template-modal.component";

@Component({
  selector: 'app-email-templates-page',
  standalone: true,
  providers: [EmailsApiService, PageSizeService],
  imports: [
    ReactiveFormsModule,
    LoaderComponent,
    SearchInputComponent,
    ListGridClientSideComponent,
  ],
  templateUrl: './email-templates-page.component.html',
  styleUrls: ['../../styles/settings-styles.scss'],
})
export class EmailTemplatesPageComponent extends CommonPageComponent implements OnInit {
  private emailTemplatesService = inject(EmailsApiService);
  private modalService = inject(NgbModal);

  override readonly headerData: IPartialHeaderData = {
    title: 'Email Templates',
    iconClass: 'ic-mail-20',
    backPath: ['/settings']
  };
  searchControl = new FormControl<string | null>(null);
  editEmailTemplateModalOpened = false;
  defaultTemplates: Record<string, TemplateSettingsBase> = {};
  templates = signal<EmailTemplate[]>([]);
  searchControlSignal = toSignal(this.searchControl.valueChanges);
  filteredTemplatesList = computed(() => {
    const searchValue = this.searchControlSignal()?.toLowerCase().trim();

    if (!searchValue) {
      return this.templates();
    }

    return this.templates().filter((template) => this.getTemplateHeader(template.code)?.toLowerCase().includes(searchValue));
  });

  override ngOnInit(): void {
    super.ngOnInit();
    this.setupColDefs();
    this.loadDefaultTemplates().subscribe();
    this.loadEmailTemplates().subscribe();
  }

  editDocumentTemplate(template: EmailTemplate): void {
    if (this.editEmailTemplateModalOpened) return;
    this.editEmailTemplateModalOpened = true;
    let checkIfFormWasEdited = () => false;

    const modalRef = this.modalService.open(EditEmailTemplateModalComponent, {
      size: 'xl',
      windowClass: 'edit-email-template-modal',
      beforeDismiss: () => checkIfFormWasEdited?.() ? confirm('Before closing the pop-up, please note that any unsaved changes will be lost. Do you want to proceed?') : true
    });
    const instance = modalRef.componentInstance as EditEmailTemplateModalComponent;

    checkIfFormWasEdited = () => instance.formWasEdited;
    instance.template = template;
    instance.defaultTemplate = this.defaultTemplates[EmailTemplateCodesToTypes[template.code]];

    modalRef.result.then(
      () => this.loadEmailTemplates().subscribe(() => this.editEmailTemplateModalOpened = false),
      () => this.editEmailTemplateModalOpened = false
    );
  }

  private loadEmailTemplates(): Observable<EmailTemplate[]> {
    this.isLoading.set(true);

    return this.emailTemplatesService.loadEmailTemplates()
      .pipe(
        tap((templates) => this.templates.set(templates)),
        finalize(() => this.isLoading.set(false))
      );
  }

  private loadDefaultTemplates(): Observable<Record<string, TemplateSettingsBase>> {
    return this.emailTemplatesService.loadEmailDefaultTemplates()
      .pipe(tap((defaultTemplates) => this.defaultTemplates = defaultTemplates));
  }

  private getTemplateHeader(code: string): string {
    return EmailTemplatesHelper.TemplateHeadersRecord[code] ?? code;
  }

  // =====  Grid  =====

  readonly emailTemplatesRowSelection = BaseListGridComponent.noCheckboxesRowSelection;

  colDefs: ColDef[] = [];

  private setupColDefs(): void {
    this.colDefs = [
      {
        headerName: 'Template Name',
        flex: 2,
        field: 'code',
        sort: 'asc',
        cellRenderer: AgCoverLetterFileComponent,
        valueGetter: (params) => this.getTemplateHeader(params.data.code),
      },
      {
        sortable: false,
        width: 60,
        minWidth: 60,
        field: '',
        headerName: '',
        cellRenderer: AgEmailTemplateActionsComponent,
        cellRendererParams: {
          edit: this.editDocumentTemplate.bind(this),
        },
      },
    ]
      .map((colDef) => ({ ...colDef, suppressHeaderMenuButton: true } as ColDef));
  }
}
